import type { Signal } from '@angular/core';
import { effect } from '@angular/core';

import type { Observable } from 'rxjs';

import { createProviderToken } from '@core/common-legacy';
import type { AppContextEntity, TellentAssistantAppContext } from '@core/tellent-assistant-types';

/** Host-provided; consumed by both the conversation-starters fetch and the agent run's request context. */
export const {
  provide: provideTellentAssistantAppContext,
  inject: injectTellentAssistantAppContext,
} = createProviderToken<Observable<TellentAssistantAppContext>>('TELLENT_ASSISTANT_APP_CONTEXT')(
  false,
);

export type TellentAssistantAppContextPriority = 'page' | 'overlay';

/** `push` returns a cleanup function that feature views must call on destroy/deactivate. */
export interface TellentAssistantAppContextWriter {
  push: (entity: AppContextEntity, priority: TellentAssistantAppContextPriority) => () => void;
}

export const {
  provide: provideTellentAssistantAppContextWriter,
  inject: injectTellentAssistantAppContextWriter,
} = createProviderToken<TellentAssistantAppContextWriter>('TELLENT_ASSISTANT_APP_CONTEXT_WRITER')(
  false,
);

/** Must be called in an injection context (constructor/field initializer). */
export function registerAssistantAppContextEntity(
  entity: Signal<AppContextEntity | null>,
  priority: TellentAssistantAppContextPriority,
): void {
  const writer = injectTellentAssistantAppContextWriter({ optional: true });
  if (!writer) return;
  effect(onCleanup => {
    const current = entity();
    if (!current) return;
    onCleanup(writer.push(current, priority));
  });
}
